import ContentEditable from "../../layout/ContentEditable/ContentEditable";
import {useSetting} from "../../contexts/setting";
import {useImages} from "../../contexts/images";
import {
  getGdnLogoUrl,
  getGdnDefaultLogoColor,
  getGdnLogoIcon,
  getGdnLogoLockup,
  getGdnMasterLogoIcon,
  hasGdnFixedBrandAssets,
  shouldPreserveGdnIconColor,
  toV3LogoUrl,
} from "../../libs/gdn-logo";
import {isBlackLogoUrl} from "../../libs/logo";

/**
 * Shared logo block for the Google Display Network ad templates. One source of
 * truth for the three logo treatments the GDN formats use; each template styles
 * the emitted classes (.tpl-logo, .tpl-logo__icon, .tpl-logo__lockup,
 * .product-name) via `#TemplateId .tpl-logo …`.
 *
 *   variant="horizontal" — icon+wordmark lockup + editable "| <ProductName>"
 *                          (Inline/Large rectangle, Billboard). Needs background markers so
 *                          getGdnLogoUrl can pick the black/white marker variant.
 *   variant="stacked"    — the full designed product lockup (colored icon +
 *                          wordmark + "JETBRAINS <family>") as one SVG image
 *                          (Half-page, Skyscraper, Leaderboard 970). Needs
 *                          background markers so getGdnLogoLockup picks the black/white
 *                          wordmark variant. The product name is baked into the
 *                          lockup here — it is NOT editable in these formats.
 *   variant="icon"       — product icon only (Leaderboard 468, GDN Japan).
 *   variant="responsive-horizontal" — centered JetBrains IDEs + editable
 *                          product lockup used by responsive layouts.
 *   variant="responsive-stacked" — the dynamic product lockup inside its
 *                          responsive Figma frame and oversized asset box.
 *
 * Existing product logo variants are v3-served (see libs/gdn-logo.ts). The
 * Editable variants reuse Social's `productNames` edits field and the same
 * font-color shortcuts as the title and subtitle.
 */
const GdnLogoBlock = ({
  id,
  variant = "horizontal",
  productName,
  hideProductName = false,
  bgMarkers,
  accentColor,
  onAccentColorUsageChange,
}) => {
  const {product} = useSetting();
  const {logoVariants} = useImages();
  const hasFixedBrandAssets = hasGdnFixedBrandAssets(product.value);
  const logoOverride = hasFixedBrandAssets ? null : logoVariants[id] || null;
  const defaultLogoColor = getGdnDefaultLogoColor(product.value);
  const logoColor = logoOverride
    ? isBlackLogoUrl(logoOverride)
      ? "black"
      : "light"
    : defaultLogoColor;
  const logoColorClass = logoColor ? ` gdn-logo-cycle-${logoColor}` : "";
  const preserveIconColorClass = shouldPreserveGdnIconColor(product.value)
    ? " tpl-logo__preserve-icon-color"
    : "";
  const iconColorOverride =
    logoColor === "black" ? "black" : logoColor === "light" ? "white" : undefined;
  const cycleTargetAttributes = hasFixedBrandAssets
    ? {}
    : {
        "data-logo-cycle-target": "gdn",
        "data-tooltip": "Option+click to recolor",
      };
  const cycleTooltipClass = hasFixedBrandAssets ? "" : " instant-tooltip";

  if (variant === "icon") {
    return (
      <div className="tpl-logo tpl-logo--icon">
        <div
          {...cycleTargetAttributes}
          className={`tpl-logo__cycle-target${cycleTooltipClass}${logoColorClass}`}
        >
          <img
            className={`tpl-logo__icon${preserveIconColorClass}`}
            src={getGdnLogoIcon(product.value, bgMarkers, iconColorOverride)}
            alt={product.label}
          />
        </div>
      </div>
    );
  }

  if (variant === "stacked") {
    return (
      <div className="tpl-logo tpl-logo--stacked">
        <div
          {...cycleTargetAttributes}
          className={`tpl-logo__cycle-target${cycleTooltipClass}${logoColorClass}`}
        >
          <img
            className="tpl-logo__lockup"
            src={getGdnLogoLockup(
              product.value,
              bgMarkers,
              logoColor === "black" ? "black" : logoColor === "light" ? "white" : undefined
            )}
            alt={product.label}
          />
        </div>
      </div>
    );
  }

  if (variant === "responsive-stacked") {
    return (
      <div className="tpl-logo tpl-logo--responsive-stacked">
        <div
          {...cycleTargetAttributes}
          className={`tpl-logo__responsive-stacked-frame tpl-logo__cycle-target${cycleTooltipClass}${logoColorClass}`}
        >
          <img
            className="tpl-logo__lockup"
            src={getGdnLogoLockup(
              product.value,
              bgMarkers,
              logoColor === "black" ? "black" : logoColor === "light" ? "white" : undefined
            )}
            alt={product.label}
          />
        </div>
      </div>
    );
  }

  if (variant === "responsive-horizontal") {
    return (
      <div className="tpl-logo tpl-logo--responsive-horizontal">
        <div
          {...cycleTargetAttributes}
          className={`tpl-logo__responsive-lockup tpl-logo__cycle-target${cycleTooltipClass}${logoColorClass}`}
        >
          <div className="tpl-logo__responsive-icon-frame">
            <img
              className={`tpl-logo__responsive-icon${
                hasFixedBrandAssets ? "" : preserveIconColorClass
              }`}
              src={
                hasFixedBrandAssets
                  ? getGdnMasterLogoIcon(bgMarkers, iconColorOverride)
                  : getGdnLogoIcon(product.value, bgMarkers, iconColorOverride)
              }
              alt=""
            />
          </div>
          <div className="tpl-logo__responsive-text">
            <div className="tpl-logo__family">
              <strong>JETBRAINS</strong> <span>IDEs</span>
            </div>
            {!hideProductName && (
              <div className="product-name gdn-editable-product-name">
                <ContentEditable
                  id={id}
                  text={productName}
                  placeholder="Add text"
                  color={accentColor}
                  highlightStyle="text-color"
                  enableWhiteHighlightHotkey={false}
                  onAccentColorUsageChange={onAccentColorUsageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tpl-logo tpl-logo--horizontal">
      <div
        {...cycleTargetAttributes}
        className={`tpl-logo__cycle-target${cycleTooltipClass}${logoColorClass}`}
      >
        <img
          src={logoOverride ? toV3LogoUrl(logoOverride) : getGdnLogoUrl(product.value, bgMarkers)}
          alt="JetBrains"
        />
      </div>
      {!hideProductName && (
        <div className="product-name gdn-editable-product-name">
          <ContentEditable
            id={id}
            text={productName}
            placeholder="Add text"
            color={accentColor}
            highlightStyle="text-color"
            enableWhiteHighlightHotkey={false}
            onAccentColorUsageChange={onAccentColorUsageChange}
          />
        </div>
      )}
    </div>
  );
};

export default GdnLogoBlock;
